import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigModel } from '../../Core/Config/models/config.model';
import { ConfigService } from '../../Core/Config/services/config.service';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { MediaServiceAccessTypeEnum } from './enums/media-service-access-type.enum';
import { MediaResultsModel, MediaMetadataResultsModel } from './mediaResults.api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { MediaApi, MediaNoteApi, MediaSummaryApi } from '../../api/contentLibrary/models/content-library-media.api.model';
import { MultiBaseAbstractApi } from '../../api/genericApi/multi-base-abstract.api';
import { AccountSettingsApi, AccountStatusApi } from '../../api/contentLibrary/models/account-status.api.model';

@Injectable()
export class MediaServiceApi extends MultiBaseAbstractApi<MediaApi, MediaSummaryApi>{
    private apiUrl: string;

    constructor(
        private neptuneApi: NeptuneApi,
        private configurationService: ConfigService,
    ) {
        super();
        const config: ConfigModel = this.configurationService.getConfig();
        const hasConfig: boolean = config !== null && typeof config === 'object';

        if (hasConfig) {
            this.apiUrl = config.apiUrl;
        }
    }

    upload(
        files: File[],
        accessType: MediaServiceAccessTypeEnum = MediaServiceAccessTypeEnum.PRIVATE,
        isContentLibrary?: string
    ): Observable<MediaResultsModel> {
        const slug: string = '/media';
        const formData: FormData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('file', files[i], files[i].name);
        }
        formData.append('accessType', accessType);
        if (isContentLibrary) {
            formData.append('isContentLibrary', isContentLibrary);
        }

        const url = this.apiUrl + slug;

        // Need to bypass the application/json content-type so just call it directly
        return this.neptuneApi.httpClient.request(
            HttpVerbsEnum.POST.toString(),
            url,
            {
                body: formData,
                withCredentials: true
            }
        );
    }

    create(resource: MediaApi): Observable<number> {
        return;
    }

    delete(id: string): Observable<void> {
        const slug: string = `/media/${id}`;
        return this.neptuneApi.api(slug, null, HttpVerbsEnum.DELETE);
    }

    getFiles(ids: string[]): Observable<MediaMetadataResultsModel> {
        const slug: string = `/media?ids=${ids.join(',')}`;
        return this.neptuneApi.api(slug, null, HttpVerbsEnum.GET);
    }

    getAccountStatus(): Observable<AccountStatusApi> {
        return this.neptuneApi.api<AccountStatusApi>(
            '/content/accounts/status',
            undefined,
            HttpVerbsEnum.GET
        );
    }

    getAccountStatusById(accountId: number): Observable<AccountStatusApi> {
        return this.neptuneApi.api<AccountStatusApi>(
            `/content/admin/accounts/${accountId}`,
            undefined,
            HttpVerbsEnum.GET
        );
    }

    updateStorageLimit(accountId: number, payload: AccountSettingsApi): Observable<null> {
        return this.neptuneApi.api<null>(
            `/content/admin/accounts/${accountId}`,
            payload,
            HttpVerbsEnum.PUT
        );
    }

    getMedia(contentId: string): Observable<MediaApi> {
        return this.neptuneApi.api<MediaApi>(
            `/content/${contentId}`,
            undefined,
            HttpVerbsEnum.GET
        );
    }

    getList(): Observable<MediaSummaryApi[]> {  // Change return type to array
        return this.neptuneApi.api<MediaSummaryApi[]>(  // Expect array response
            '/content',  // No trailing slash
            undefined,
            HttpVerbsEnum.GET
        );
    }

    update(model: MediaApi): Observable<number> {
        return this.neptuneApi.api<number>(
            `/content/${model.id}`,
            model,
            HttpVerbsEnum.PUT
        );
    }

    createNote(contentId: string, note: MediaNoteApi): Observable<number> {
        return this.neptuneApi.api<number>(
            `/content/${contentId}/notes`,
            note,
            HttpVerbsEnum.POST
        );
    }

    updateNote(contentId: string, noteId: number, note: MediaNoteApi): Observable<number> {
        return this.neptuneApi.api<number>(
            `/content/${contentId}/notes/${noteId}`,
            note,
            HttpVerbsEnum.PUT
        );
    }

    deleteNote(contentId: string, noteId: number): Observable<number> {
        return this.neptuneApi.api<number>(
            `/content/${contentId}/notes/${noteId}`,
            undefined,
            HttpVerbsEnum.DELETE
        );
    }

    approveMedia(contentId: string): Observable<number> {
        return this.neptuneApi.api<number>(
            `/content/${contentId}/approve`,
            undefined,
            HttpVerbsEnum.PUT
        );
    }
}
